import { logger } from 'common/services';
import { uuid } from 'common/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { keycloakService } from 'modules/keycloak';

import type { MeResponse, PermissionsFailureClass, PermissionsQueryError, PermissionsScope } from './types';

/**
 * `pinFetch` in `reportingApi` is the model for this transport, and for the same reason:
 * `ajaxService`'s response interceptor calls `keycloak.logout()` on *any* 401. A permission check
 * is the one request whose 401 must not sign anybody out (R5) - it denies, it reports, and the
 * user stays exactly where they were.
 *
 * Unlike `pinFetch`, this one is bounded. `pinFetch` has no timeout and axios's 60s default does
 * not apply to a bare `fetch`, so a hung request would leave every gate on the page pending for
 * as long as the socket stayed open.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Refresh if the token expires within the minute, matching `updateKeycloakToken`'s window.
 *
 * `keycloakService.updateToken` is called **directly** rather than through that wrapper, whose
 * `catch` calls `keycloak.logout()`. Routing this through it would turn a permissions-service
 * blip into a sign-out - the precise failure R5 exists to prevent.
 */
const TOKEN_MIN_VALIDITY_SECONDS = 60;

/**
 * One place where a failure becomes both a query error and a report, so the two cannot drift and
 * a new failure path cannot be added that denies silently.
 */
const fail = (
  status: number,
  failureClass: PermissionsFailureClass,
  scope: PermissionsScope
): { error: PermissionsQueryError } => {
  logger.error('[permissions] Could not resolve permissions', {
    scopeKind: scope.kind,
    status,
    failureClass,
  });

  return { error: { status, data: { failureClass } } };
};

/** An abort is ordinary - a gate unmounted, or the scope changed - so it denies without reporting. */
const ABORTED: { error: PermissionsQueryError } = { error: { status: 499, data: { failureClass: 'network' } } };

const queryFor = (scope: PermissionsScope) =>
  scope.kind === 'community'
    ? `communityId=${encodeURIComponent(scope.communityId)}`
    : `organisationId=${encodeURIComponent(scope.organisationId)}`;

/**
 * `GET /v1/permissions/me` for one scope.
 *
 * Every failure resolves to an error result. Nothing here throws: a throw inside RTK Query's
 * `queryFn` becomes an unhandled rejection rather than a denial, and the permission layer can
 * only fail closed if it is actually told the answer is undetermined.
 */
export const fetchMyPermissions = async (
  scope: PermissionsScope,
  upstreamSignal: AbortSignal
): Promise<{ data: MeResponse } | { error: PermissionsQueryError }> => {
  try {
    await keycloakService.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
  } catch {
    // The caught value is deliberately dropped rather than logged: a Keycloak refresh error can
    // carry the refresh token and the full token endpoint response.
    return fail(401, 'refresh-rejected', scope);
  }

  const token = keycloakService.token;
  // A resolved refresh with no token means there is no session to ask about. Same class, because
  // the consequence is identical and a second name for it would imply a branch that is not there.
  if (!token) return fail(401, 'refresh-rejected', scope);

  /**
   * The request budget, composed by hand rather than with `AbortSignal.any` +
   * `AbortSignal.timeout`. Two reasons, both load-bearing:
   *
   * - Those two landed in Chrome 116 and Safari 17.4, this app's `browserslist` is `>0.2%, not
   *   dead`, and `build.target` is `esnext` - so Vite would ship the call untranspiled and an
   *   older browser would throw `AbortSignal.any is not a function`. That throw would escape as
   *   an unhandled rejection instead of denying, which is the one thing this function promises
   *   not to do.
   * - `AbortSignal.any` reports only that it aborted, not which input did, so telling a timeout
   *   from an unmount needs a flag either way.
   */
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  const onUpstreamAbort = () => controller.abort();
  // An already-aborted upstream signal never fires `abort` again, so check before subscribing.
  if (upstreamSignal.aborted) controller.abort();
  else upstreamSignal.addEventListener('abort', onUpstreamAbort, { once: true });

  // The budget covers reading the body too, not just the headers: a response that stalls
  // mid-stream hangs the gate exactly as a response that never arrives does.
  try {
    let response: Response;

    try {
      response = await fetch(`${configService.getUrl(UrlNames.PermissionsApi)}/v1/permissions/me?${queryFor(scope)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          // Matches what `ajaxService` attaches, so one request can be followed across services.
          'X-Request-ID': uuid(),
        },
        // The browser's own cache only. It emits no header an intermediary honours, so it is not
        // what keeps one user's grants from being served to another - that is the service's
        // `Cache-Control: private, no-store` and `Vary: Authorization`, which U1's probe checks.
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch {
      if (timedOut) return fail(504, 'timeout', scope);
      if (upstreamSignal.aborted) return ABORTED;

      // `fetch` rejects with an opaque `TypeError` for a DNS failure, an offline browser and a
      // blocked CORS preflight alike - the browser deliberately withholds which. So this class
      // cannot be narrowed here, and a CORS misconfiguration presents as a network outage.
      return fail(0, 'network', scope);
    }

    if (response.status === 401) return fail(401, 'unauthorized', scope);
    if (!response.ok) return fail(response.status, 'http', scope);

    // Read as text and parse by hand rather than trusting `response.json()`. This transport has
    // no equivalent of `_axiosBaseQuery`'s SPA-shell check, which exists because an edge misroute
    // answers with the app's own HTML under a 200 - and a permission response that silently
    // parsed to nothing would read as "no grants", denying everything while looking valid.
    let body: string;

    try {
      body = await response.text();
    } catch {
      if (timedOut) return fail(504, 'timeout', scope);
      if (upstreamSignal.aborted) return ABORTED;

      return fail(response.status, 'non-json', scope);
    }

    if (/^\s*<(?:!doctype|html)/i.test(body)) return fail(response.status, 'non-json', scope);

    try {
      const parsed = JSON.parse(body) as MeResponse;
      // A body that parses but carries no grant list is not an answer. Without this it would
      // reach `toAllowedActions` as `undefined` and throw inside `queryFn`.
      if (!Array.isArray(parsed.allowed)) return fail(response.status, 'non-json', scope);

      return { data: parsed };
    } catch {
      return fail(response.status, 'non-json', scope);
    }
  } finally {
    clearTimeout(timer);
    upstreamSignal.removeEventListener('abort', onUpstreamAbort);
  }
};
